// ./src/utils/api/MembershipService.ts
//
// Membership service — an issued pass (an instance of a MembershipType).
// OpResult-native (errors normalized in ApiService). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { BULK_JOB_FIELDS } from "./BulkJobService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Filter enums (mirror the eventsmagic-api boundary)
// =============================================================================

// A high-level expiry facet, computed server-side against CURRENT_DATE.
export const MEMBERSHIP_VALIDITY_VALUES = ["active", "expiring_soon", "expired"] as const;
export type MembershipValidity = (typeof MEMBERSHIP_VALIDITY_VALUES)[number];

// The "expiring soon" window in days (mirrors the API's
// MEMBERSHIP_EXPIRING_SOON_DAYS). Shown in the UI so "soon" is unambiguous.
export const MEMBERSHIP_EXPIRING_SOON_DAYS = 30;

// A membership's derived validity from its expiry date (client-side, for
// coloring the list + the CSV status column). "perpetual" = no expiry.
export type MembershipValidityState = MembershipValidity | "perpetual";

export const getMembershipValidity = (
  expiresOn?: string | null
): MembershipValidityState => {
  if (!expiresOn) {
    return "perpetual";
  }

  const parts = expiresOn.split("-").map(Number);
  const exp = new Date(parts[0], (parts[1] ?? 1) - 1, parts[2] ?? 1);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const soon = new Date(today);
  soon.setDate(soon.getDate() + MEMBERSHIP_EXPIRING_SOON_DAYS);

  if (exp < today) {
    return "expired";
  }

  if (exp <= soon) {
    return "expiring_soon";
  }

  return "active";
};

// Bulk-generate limit (mirrors the API's MEMBERSHIP_MAX_GENERATE).
export const MEMBERSHIP_MAX_GENERATE = 500;

// Purge scope (mirrors the API's MEMBERSHIP_PURGE_SCOPE).
export const MEMBERSHIP_PURGE_SCOPE_VALUES = ["all", "internal", "external"] as const;
export type MembershipPurgeScope = (typeof MEMBERSHIP_PURGE_SCOPE_VALUES)[number];

// ─── Import (#271) — mirrors the API's boundary/membershipImport.ts; extend both together ───

/** What became of one imported row. */
export const MEMBERSHIP_IMPORT_ROW_OUTCOME = {
  CREATE: "create",
  UPDATE: "update",
  SKIP: "skip",
} as const;

/** Why a row was refused — a code, worded by the import's status card. */
export const MEMBERSHIP_IMPORT_SKIP_REASON = {
  UNKNOWN_TYPE: "unknown_type",
  NO_NUMBER: "no_number",
  BAD_DATE: "bad_date",
  EXPIRES_BEFORE_START: "expires_before_start",
  TYPE_GONE: "type_gone",
} as const;

/** Why a whole import stopped early (the job's lastError). */
export const MEMBERSHIP_IMPORT_STOP_REASON = {
  PLAN_REQUIRED: "plan_required",
} as const;

/** How a slashed date (03/04/2026) reads. */
export const MEMBERSHIP_IMPORT_DATE_ORDER = {
  MDY: "mdy",
  DMY: "dmy",
} as const;

export type MembershipImportDateOrder = (typeof MEMBERSHIP_IMPORT_DATE_ORDER)[keyof typeof MEMBERSHIP_IMPORT_DATE_ORDER];

/** One row of a member list, after the file's headers are mapped. Every field optional; blank = not given. */
export interface MembershipImportRow {
  membershipNumber?: string | null;
  membershipTypeName?: string | null;
  startsOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
  company?: string | null;
  contactName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  jobTitle?: string | null;
}

export type MembershipImportField = keyof MembershipImportRow;

// =============================================================================
// GraphQL
// =============================================================================

const MEMBERSHIP_FIELDS = `
  id
  accountId
  membershipTypeId
  personGroupId
  personGroup { id name kind }
  membershipNumber
  startsOn
  expiresOn
  notes
  status
  usesCount
  extraUses
  accountOrderLineId
  isTest
  rights
  createdAt
  updatedAt
`;

const MEMBERSHIP_LIST_QUERY = gql`
  query MembershipList($filter: MembershipFilter, $params: PaginationAndSorting) {
    membershipList(filter: $filter, params: $params) {
      code
      data { ${MEMBERSHIP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

// #281 — the online purchase behind ONE membership (three server reads): the drawer's refund, never a list.
const MEMBERSHIP_PURCHASE_QUERY = gql`
  query MembershipPurchase($id: ID) {
    membershipGet(id: $id) {
      code
      data {
        id
        purchase {
          orderId referenceCode paidAt totalAmount refundedAmount currencyCode paymentStatus isTest buyerName buyerEmail
          recurringGiftId recurringStatus billingInterval nextChargeAt
        }
      }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_GET_QUERY = gql`
  query MembershipGet($id: ID) {
    membershipGet(id: $id) {
      code
      data { ${MEMBERSHIP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_GET_MANY_QUERY = gql`
  query MembershipGetMany($ids: [ID]) {
    membershipGetMany(ids: $ids) {
      code
      data { ${MEMBERSHIP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_CREATE_MUTATION = gql`
  mutation MembershipCreate($params: MembershipInput) {
    membershipCreate(params: $params) {
      code
      data { ${MEMBERSHIP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_UPDATE_MUTATION = gql`
  mutation MembershipUpdate($where: MembershipWhereInput, $params: MembershipInput) {
    membershipUpdate(where: $where, params: $params) {
      code
      data { ${MEMBERSHIP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_REMOVE_MUTATION = gql`
  mutation MembershipRemove($where: MembershipWhereInput) {
    membershipRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_GENERATE_MUTATION = gql`
  mutation MembershipGenerate($params: MembershipGenerateInput) {
    membershipGenerate(params: $params) {
      code
      data { ${MEMBERSHIP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const MEMBERSHIP_IMPORT_START_MUTATION = gql`
  mutation MembershipImportStart($params: MembershipImportStartInput) {
    membershipImportStart(params: $params) {
      code
      errors { name errors warnings }
      data { ${BULK_JOB_FIELDS} }
    }
  }
`;

const MEMBERSHIP_PURGE_MUTATION = gql`
  mutation MembershipPurge($params: MembershipPurgeInput) {
    membershipPurge(params: $params) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Membership schema)
// =============================================================================

export interface Membership {
  id?: string;
  accountId?: string;
  membershipTypeId?: string | null;
  personGroupId?: string | null;
  /** #271 — who holds it: the member business or household it covers (read live; null when none or removed). */
  personGroup?: { id: string; name?: string | null; kind?: string | null } | null;
  membershipNumber?: string | null;
  startsOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
  status?: number;
  /** Uses spent — admissions bought with it that still stand + guests holding a place on a members-only RSVP. */
  usesCount?: number | null;
  /** #287 — uses repeat purchases added on top of the type's limit. */
  extraUses?: number | null;
  /** #281 — the order line that sold it online (null = issued by hand or imported); its purchase is `purchaseOf`. */
  accountOrderLineId?: string | null;
  /** #281 — bought with a test card: numbered TEST-…, admits only on events in test mode. */
  isTest?: boolean | null;
  /** Actions the current caller may perform on this record ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

/** #281 — the online order that sold (or last renewed) a membership. Refund it with `DonationService.refund({ orderId })`. */
export interface MembershipPurchase {
  orderId: string;
  referenceCode?: string | null;
  paidAt?: string | null;
  totalAmount?: number | null;
  refundedAmount?: number | null;
  currencyCode?: string | null;
  /** succeeded | partially_refunded | refunded */
  paymentStatus?: string | null;
  isTest?: boolean | null;
  buyerName?: string | null;
  buyerEmail?: string | null;
  /** #204 step 6b — it renews automatically: the subscription (stop it with `RecurringGiftService.cancel`). */
  recurringGiftId?: string | null;
  /** incomplete | active | past_due | canceled */
  recurringStatus?: string | null;
  /** month | quarter | year */
  billingInterval?: string | null;
  nextChargeAt?: string | null;
}

export interface MembershipInput {
  /** #287 — uses on top of the type's limit. */
  extraUses?: number | null;
  membershipTypeId?: string | null;
  personGroupId?: string | null;
  membershipNumber?: string | null;
  startsOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
  status?: number | null;
}

export interface MembershipFilter {
  id?: string[] | null;
  membershipTypeId?: string[] | null;
  personGroupId?: string[] | null;
  membershipNumber?: string[] | null;
  /** Expiry facet: 'active' | 'expiring_soon' | 'expired' (computed server-side). */
  validity?: MembershipValidity | null;
  /** Filter by whether the membership's type is external (imported) or internal. */
  isExternal?: boolean | null;
  /** #281 — only test memberships (true) or only real ones (false). */
  isTest?: boolean | null;
  searchKeyword?: string | null;
}

export interface MembershipWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// A bulk update (>1 id) may only mass-change these fields server-side.
export interface MembershipBulkInput {
  status?: number | null;
  expiresOn?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class MembershipService {
  /** #281 — the online purchase behind one membership (null data when it was not bought online). Always from the server. */
  static async purchaseOf(id: string): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_PURCHASE_QUERY, { id }, { fetchPolicy: "network-only" });
  }

  static async list(
    filter?: MembershipFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches every membership matching `filter` by paging through the list. Used
   * for CSV export. Returns OpResult.ok(allRows) on success, or the first failing
   * page's OpResult. Paged by
   * `listAllPages`: above 50,000 rows it REFUSES rather than cuts, unless `maxRows` states a stop (ROADMAP #118).
   */
  static async listAll(
    filter?: MembershipFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<Membership>(MembershipService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(MEMBERSHIP_GET_MANY_QUERY, { ids });
  }

  static async create(params: MembershipInput): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_CREATE_MUTATION, { params });
  }

  static async update(
    where: MembershipWhereInput,
    params: MembershipInput
  ): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_REMOVE_MUTATION, { where: { id } });
  }

  /** Bulk-issue `count` memberships for an internal membership type. */
  static async generate(membershipTypeId: string, count: number): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_GENERATE_MUTATION, {
      params: { membershipTypeId, count },
    });
  }

  /**
   * #271 — hand a member list WHOLE to the server, which imports it in the background (a `BulkJob` of kind
   * `membership_import`). Answers with the job at once; `useBulkJobs` follows it.
   */
  static async startImport(params: {
    membershipTypeId?: string | null;
    dateOrder: MembershipImportDateOrder;
    rows: MembershipImportRow[];
  }): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_IMPORT_START_MUTATION, { params });
  }

  /** Remove expired memberships, optionally scoped to internal/external types. */
  static async purge(scope: MembershipPurgeScope = "all"): Promise<OpResult> {
    return ApiService.executeMutation(MEMBERSHIP_PURGE_MUTATION, { params: { scope } });
  }

  /**
   * Mass-changes `status` and/or `expiresOn` on every selected membership in one
   * call (the server restricts a multi-id update to this field subset). Pass only
   * the fields to change.
   */
  static async bulkUpdate(ids: string[], params: MembershipBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(MEMBERSHIP_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected membership in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(MEMBERSHIP_REMOVE_MUTATION, { where: { id: ids } });
  }
}
